import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';

import AdminProtectedRoute from '../../../components/admin/AdminProtectedRoute';
import AdminLayout from '../../../components/admin/AdminLayout';
import StreamBuilder from '../../../components/common/StreamBuilder';
import { BlocManagementUsers, UserViewState } from '../../../bloc/BlocManagementUsers';
import { User } from '../../../../types/cms.types';
import { CpnCreateEditUser } from './components/CpnCreateEditUser';

export const UsersManagementPage: React.FC = () => {
    // Bloc instance
    const [bloc] = useState(() => new BlocManagementUsers());

    // UI state
    const [selectedCity, setSelectedCity] = useState<string>('');
    const [selectedActiveStatus, setSelectedActiveStatus] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [lazyLoading, setLazyLoading] = useState<boolean>(false);

    // Dialog states
    const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
    const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);

    // Refs
    const toast = useRef<Toast>(null);

    // Load data on component mount
    useEffect(() => {
        bloc.initData();

        // Cleanup on unmount
        return () => {
            bloc.dispose();
        };
    }, [bloc]);

    // City options
    const cityOptions = [
        { label: 'Tất cả thành phố', value: '' },
        { label: 'An Giang', value: 'An Giang' },
        { label: 'Bà Rịa - Vũng Tàu', value: 'Bà Rịa - Vũng Tàu' },
        { label: 'Bắc Giang', value: 'Bắc Giang' },
        { label: 'Bắc Kạn', value: 'Bắc Kạn' },
        { label: 'Bạc Liêu', value: 'Bạc Liêu' },
        { label: 'Bắc Ninh', value: 'Bắc Ninh' },
        { label: 'Bến Tre', value: 'Bến Tre' },
        { label: 'Bình Định', value: 'Bình Định' },
        { label: 'Bình Dương', value: 'Bình Dương' },
        { label: 'Bình Phước', value: 'Bình Phước' },
        { label: 'Bình Thuận', value: 'Bình Thuận' },
        { label: 'Cà Mau', value: 'Cà Mau' },
        { label: 'Cần Thơ', value: 'Cần Thơ' },
        { label: 'Cao Bằng', value: 'Cao Bằng' },
        { label: 'Đà Nẵng', value: 'Đà Nẵng' },
        { label: 'Đắk Lắk', value: 'Đắk Lắk' },
        { label: 'Đắk Nông', value: 'Đắk Nông' },
        { label: 'Điện Biên', value: 'Điện Biên' },
        { label: 'Đồng Nai', value: 'Đồng Nai' },
        { label: 'Đồng Tháp', value: 'Đồng Tháp' },
        { label: 'Gia Lai', value: 'Gia Lai' },
        { label: 'Hà Giang', value: 'Hà Giang' },
        { label: 'Hà Nam', value: 'Hà Nam' },
        { label: 'Hà Nội', value: 'Hà Nội' },
        { label: 'Hà Tĩnh', value: 'Hà Tĩnh' },
        { label: 'Hải Dương', value: 'Hải Dương' },
        { label: 'Hải Phòng', value: 'Hải Phòng' },
        { label: 'Hậu Giang', value: 'Hậu Giang' },
        { label: 'TP.HCM', value: 'Hồ Chí Minh' },
        { label: 'Hòa Bình', value: 'Hòa Bình' },
        { label: 'Hưng Yên', value: 'Hưng Yên' },
        { label: 'Khánh Hòa', value: 'Khánh Hòa' },
        { label: 'Kiên Giang', value: 'Kiên Giang' },
        { label: 'Kon Tum', value: 'Kon Tum' },
        { label: 'Lai Châu', value: 'Lai Châu' },
        { label: 'Lâm Đồng', value: 'Lâm Đồng' },
        { label: 'Lạng Sơn', value: 'Lạng Sơn' },
        { label: 'Lào Cai', value: 'Lào Cai' },
        { label: 'Long An', value: 'Long An' },
        { label: 'Nam Định', value: 'Nam Định' },
        { label: 'Nghệ An', value: 'Nghệ An' },
        { label: 'Ninh Bình', value: 'Ninh Bình' },
        { label: 'Ninh Thuận', value: 'Ninh Thuận' },
        { label: 'Phú Thọ', value: 'Phú Thọ' },
        { label: 'Phú Yên', value: 'Phú Yên' },
        { label: 'Quảng Bình', value: 'Quảng Bình' },
        { label: 'Quảng Nam', value: 'Quảng Nam' },
        { label: 'Quảng Ngãi', value: 'Quảng Ngãi' },
        { label: 'Quảng Ninh', value: 'Quảng Ninh' },
        { label: 'Quảng Trị', value: 'Quảng Trị' },
        { label: 'Sóc Trăng', value: 'Sóc Trăng' },
        { label: 'Sơn La', value: 'Sơn La' },
        { label: 'Tây Ninh', value: 'Tây Ninh' },
        { label: 'Thái Bình', value: 'Thái Bình' },
        { label: 'Thái Nguyên', value: 'Thái Nguyên' },
        { label: 'Thanh Hóa', value: 'Thanh Hóa' },
        { label: 'Thừa Thiên Huế', value: 'Thừa Thiên Huế' },
        { label: 'Tiền Giang', value: 'Tiền Giang' },
        { label: 'Trà Vinh', value: 'Trà Vinh' },
        { label: 'Tuyên Quang', value: 'Tuyên Quang' },
        { label: 'Vĩnh Long', value: 'Vĩnh Long' },
        { label: 'Vĩnh Phúc', value: 'Vĩnh Phúc' },
        { label: 'Yên Bái', value: 'Yên Bái' }
    ];

    // Active status options
    const activeStatusOptions = [
        { label: 'Tất cả', value: '' },
        { label: 'Đang hoạt động', value: 'true' },
        { label: 'Đã khóa', value: 'false' }
    ];

    // Filter handlers
    const handleCityFilter = async (city: string) => {
        setSelectedCity(city);
        await bloc.filterByCity(city);
    };

    const handleActiveStatusFilter = async (status: string) => {
        setSelectedActiveStatus(status);
        const isActive = status === 'true' ? true : status === 'false' ? false : undefined;
        await bloc.filterByActiveStatus(isActive);
    };

    const handleSearch = async () => {
        await bloc.searchUsers(searchTerm);
    };

    const handleSearchKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // Clear filters
    const clearFilters = async () => {
        setSelectedCity('');
        setSelectedActiveStatus('');
        setSearchTerm('');
        await bloc.clearFilters();
    };

    // Page change handler
    const onPageChange = async (event: any) => {
        setLazyLoading(true);

        if (event.rows !== bloc.currentPagination.limit) {
            await bloc.changePageSize(event.rows);
        } else {
            await bloc.changePage(event.page + 1);
        }

        setLazyLoading(false);
    };

    // Refresh handler
    const handleRefresh = async () => {
        await bloc.refresh();
    };

    // Delete handler
    const handleDelete = (user: User) => {
        confirmDialog({
            message: `Bạn có chắc chắn muốn xóa người dùng "${user.full_name}"?`,
            header: 'Xác nhận xóa',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    await bloc.deleteUser(user.id);
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Thành công',
                        detail: 'Đã xóa người dùng thành công',
                        life: 3000
                    });
                } catch (error: any) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Lỗi',
                        detail: error.message || 'Không thể xóa người dùng',
                        life: 3000
                    });
                }
            }
        });
    };

    // Toggle active status
    const handleToggleStatus = (user: User) => {
        const newStatus = !user.is_active;
        const action = newStatus ? 'kích hoạt' : 'khóa';

        confirmDialog({
            message: `Bạn có chắc chắn muốn ${action} tài khoản "${user.full_name}"?`,
            header: `Xác nhận ${action}`,
            icon: 'pi pi-question-circle',
            accept: async () => {
                try {
                    await bloc.toggleUserStatus(user.id, newStatus);
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Thành công',
                        detail: `Đã ${action} tài khoản thành công`,
                        life: 3000
                    });
                } catch (error: any) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Lỗi',
                        detail: error.message || `Không thể ${action} tài khoản`,
                        life: 3000
                    });
                }
            }
        });
    };

    // Dialog handlers
    const handleCreateUser = () => {
        setShowCreateDialog(true);
    };

    const handleEditUser = (user: User) => {
        setSelectedUser(user);
        setShowEditDialog(true);
    };

    const handleCloseEditDialog = () => {
        setShowEditDialog(false);
        setSelectedUser(null);
    };

    const handleCloseCreateDialog = () => {
        setShowCreateDialog(false);
    };

    const handleSaveUser = async (userData: any) => {
        try {
            if (selectedUser) {
                await bloc.updateUser(selectedUser.id, userData);
                toast.current?.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: 'Đã cập nhật người dùng thành công',
                    life: 3000
                });
            }
            handleCloseEditDialog();
        } catch (error: any) {
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: error.message || 'Không thể cập nhật người dùng',
                life: 3000
            });
        }
    };

    const handleCreateUserSave = async (userData: any) => {
        try {
            await bloc.createUser(userData);
            toast.current?.show({
                severity: 'success',
                summary: 'Thành công',
                detail: 'Đã tạo người dùng thành công',
                life: 3000
            });
            handleCloseCreateDialog();
        } catch (error: any) {
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: error.message || 'Không thể tạo người dùng',
                life: 3000
            });
        }
    };

    // Column templates
    const userInfoTemplate = (user: User) => (
        <div>
            <div className="flex items-center gap-3">
                {user.url_avatar && (
                    <img
                        src={user.url_avatar}
                        alt={user.full_name}
                        className="w-10 h-10 rounded-full object-cover"
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/40';
                        }}
                    />
                )}
                <div>
                    <div className="font-medium text-gray-900">{user.full_name}</div>
                    <div className="text-sm text-gray-500">{user.phone_number}</div>
                    {user.email && (
                        <div className="text-sm text-gray-500">{user.email}</div>
                    )}
                </div>
            </div>
        </div>
    );

    const locationTemplate = (user: User) => (
        <div className="text-sm">
            {user.location_city || 'Chưa xác định'}
        </div>
    );

    const deviceTemplate = (user: User) => (
        <div>
            {user.device && (
                <div className="text-sm text-gray-600">{user.device}</div>
            )}
            {user.version_current_app && (
                <div className="text-xs text-gray-400">v{user.version_current_app}</div>
            )}
            {!user.device && !user.version_current_app && (
                <span className="text-sm text-gray-400">Chưa có</span>
            )}
        </div>
    );

    const statusTemplate = (user: User) => (
        <Tag
            value={user.is_active ? 'Hoạt động' : 'Đã khóa'}
            severity={user.is_active ? 'success' : 'danger'}
            icon={user.is_active ? 'pi pi-check-circle' : 'pi pi-times-circle'}
        />
    );

    const dateTemplate = (user: User) => (
        <div className="text-sm text-gray-600">
            {new Date(user.created_at).toLocaleDateString('vi-VN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            })}
        </div>
    );

    const actionsTemplate = (user: User) => (
        <div className="flex gap-2">
            <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-text p-button-info"
                tooltip="Chỉnh sửa"
                tooltipOptions={{ position: 'top' }}
                onClick={() => handleEditUser(user)}
            />
            <Button
                icon={user.is_active ? 'pi pi-lock' : 'pi pi-lock-open'}
                className={`p-button-rounded p-button-text ${user.is_active ? 'p-button-warning' : 'p-button-success'}`}
                tooltip={user.is_active ? 'Khóa tài khoản' : 'Kích hoạt tài khoản'}
                tooltipOptions={{ position: 'top' }}
                onClick={() => handleToggleStatus(user)}
            />
            <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-text p-button-danger"
                tooltip="Xóa"
                tooltipOptions={{ position: 'top' }}
                onClick={() => handleDelete(user)}
            />
        </div>
    );


    const headerTemplate = (
        <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            alignItems: 'center',
            padding: '16px 24px',
            borderBottom: '1px solid #e5e7eb',
            background: '#f9fafb',
            width: '100%'
        }}>
            {/* Search */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 300px', minWidth: '300px' }}>
                <i className="pi pi-search" style={{ color: '#6b7280', fontSize: '0.875rem' }}></i>
                <InputText
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={handleSearchKeyPress}
                    placeholder="Tìm theo tên, SĐT, email..."
                    style={{
                        flex: 1,
                        fontSize: '0.875rem',
                        padding: '8px 12px'
                    }}
                />
                <Button
                    icon="pi pi-search"
                    onClick={handleSearch}
                    className="p-button-outlined p-button-sm"
                    style={{ padding: '6px 12px' }}
                />
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                <Button
                    icon="pi pi-filter-slash"
                    onClick={clearFilters}
                    className="p-button-outlined p-button-secondary p-button-sm"
                    tooltip="Xóa bộ lọc"
                    style={{ padding: '6px 12px' }}
                />
                <Button
                    icon="pi pi-refresh"
                    onClick={handleRefresh}
                    className="p-button-outlined p-button-sm"
                    tooltip="Làm mới"
                    style={{ padding: '6px 12px' }}
                />
            </div>
        </div>
    );

    return (
        <AdminProtectedRoute
            requiredModule="user_management"
            requiredSubModule='user_list'
            requiredPermission="view"
        >
            <AdminLayout>
                <div className="" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Toast ref={toast} />
                    <ConfirmDialog />

                    <div style={{ flex: 1, minHeight: 0, background: 'white' }}>
                        <StreamBuilder
                            stream={bloc.getStream('info')}
                            initialData={bloc.info}
                            builder={(snapshot: any) => {
                                const state: UserViewState = snapshot.data || bloc.info;

                                return (
                                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        {/* Table */}
                                        <div style={{ flex: 1, overflow: 'hidden' }}>
                                            <DataTable
                                                scrollHeight='calc(100vh - 200px)'
                                                value={state.users}
                                                loading={state.loading || lazyLoading}
                                                loadingIcon="pi pi-spinner pi-spin"
                                                className="users-management-table"
                                                emptyMessage="Không tìm thấy người dùng nào"
                                                responsiveLayout="scroll"
                                                scrollable
                                                size="small"
                                                stripedRows
                                                header={headerTemplate}
                                                showGridlines
                                                paginator={state.pagination.total > 0}
                                                rows={state.pagination.limit}
                                                totalRecords={state.pagination.total}
                                                first={(state.pagination.page - 1) * state.pagination.limit}
                                                onPage={onPageChange}
                                                rowsPerPageOptions={[10, 25, 50, 100]}
                                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                                currentPageReportTemplate="Hiển thị {first} đến {last} trong tổng số {totalRecords} người dùng"
                                            >
                                                <Column
                                                    field="full_name"
                                                    header="Thông tin người dùng"
                                                    body={userInfoTemplate}
                                                    style={{ minWidth: '250px' }}
                                                    frozen
                                                />
                                                <Column
                                                    field="location_city"
                                                    header="Thành phố"
                                                    body={locationTemplate}
                                                    style={{ minWidth: '150px' }}
                                                    filter
                                                    showFilterMenuOptions={false}
                                                    showClearButton={false}
                                                    filterElement={
                                                        <Dropdown
                                                            value={selectedCity}
                                                            options={cityOptions}
                                                            onChange={(e) => handleCityFilter(e.value)}
                                                            placeholder="Tất cả thành phố"
                                                            style={{ width: '100%', fontSize: '0.75rem' }}
                                                            className="p-column-filter"
                                                            filter
                                                            showClear
                                                        />
                                                    }
                                                />
                                                <Column
                                                    field="device"
                                                    header="Thiết bị"
                                                    body={deviceTemplate}
                                                    style={{ minWidth: '150px' }}
                                                />
                                                <Column
                                                    field="is_active"
                                                    header="Trạng thái"
                                                    body={statusTemplate}
                                                    style={{ minWidth: '120px' }}
                                                    filter
                                                    showFilterMenuOptions={false}
                                                    showClearButton={false}
                                                    filterElement={
                                                        <Dropdown
                                                            value={selectedActiveStatus}
                                                            options={activeStatusOptions}
                                                            onChange={(e) => handleActiveStatusFilter(e.value)}
                                                            placeholder="Tất cả trạng thái"
                                                            style={{ width: '100%', fontSize: '0.75rem' }}
                                                            className="p-column-filter"
                                                        />
                                                    }
                                                />
                                                <Column
                                                    field="created_at"
                                                    header="Ngày tạo"
                                                    body={dateTemplate}
                                                    style={{ minWidth: '120px' }}
                                                />
                                                <Column
                                                    header="Thao tác"
                                                    body={actionsTemplate}
                                                    style={{ minWidth: '150px' }}
                                                    frozen
                                                    alignFrozen="right"
                                                />
                                            </DataTable>
                                        </div>

                                        {/* Edit Dialog */}
                                        {showEditDialog && selectedUser && (
                                            <CpnCreateEditUser
                                                visible={showEditDialog}
                                                onHide={handleCloseEditDialog}
                                                user={selectedUser}
                                                onSave={handleSaveUser}
                                                isCreate={false}
                                            />
                                        )}

                                        {/* Create Dialog */}
                                        {showCreateDialog && (
                                            <CpnCreateEditUser
                                                visible={showCreateDialog}
                                                onHide={handleCloseCreateDialog}
                                                user={null}
                                                onSave={handleCreateUserSave}
                                                isCreate={true}
                                            />
                                        )}
                                    </div>
                                );
                            }}
                        />

                    </div>

                </div>
            </AdminLayout>
        </AdminProtectedRoute>
    );
};

export default UsersManagementPage;
